<template>
  <div
    class="
      layout/flex:column
      layout/grid:gap:page:2
    "
    >
    <ArtistFilterForm
      :q="params.q"
      :random="params.random"
      :with-online-works="params.with_online_works"
      @form-submit="onFormSubmit"
      @search-input="onSearchInput"
      @search-reset="onSearchReset"
      @update-checked="onUpdateChecked"
      @update-sort="onUpdateSort"
    />
    <div
      v-if="params.exhibition_id > 0"
      class="
        layout/align:start
        layout/flex:column
        layout/wrapper
      "
      >
      <FilteredPaginationFilterPill
        :title="exhibitionTitle"
        aria-label="Remove exhibition pill"
        gtm="artists / remove exhibition pill"
        @removePill="onRemovePill"
      />
    </div>
    <div
      class="
        $typography/size:medium
        layout/height:cap
        layout/width:100%
        layout/wrapper
        "
      >
      <FadeTransition>
        <FilteredPaginationFilterCount
          v-if="!showSpinner"
          :filtered-total="filteredTotal"
          :label-online="i18n.t('filter-form__label__online')"
          :message-filtered-count="i18n.t('filter-form__filter_count__filtered-count', { FilteredCount: filteredTotal.toLocaleString() })"
          :message-filtered-no-count="i18n.t('filter-form__filter_count__filtered-no-count')"
          :message-search-term-no-results="i18n.t('filter-form__filter_count__no-results-for-search-term', { SearchVal: params.q })"
          :message-total-count-online="i18n.t('filter-form__filter_count__total-count-online', { TotalCount: total.toLocaleString(), Resource: 'artists' })"
          :message-total-count="i18n.t('filter-form__filter_count__total-count', { TotalCount: total.toLocaleString(), Resource: 'artists' })"
          :search-val="params.q"
          :total="total"
          data-gtm="artists / reset form"
          href="/artists/"
          @resetForm="onResetForm"
        />
        <Spinner v-if="showSpinner" />
      </FadeTransition>
    </div>
  </div>
</template>

<script>
  /**
   * FormWrapper manages the submission of the ArtistFilterForm.
   *
   * The responsibility of this component is to manage state for:
   *  - load filtered results
   *  - load paginated results
   *  - updating browser history
   */
  import Vue from 'vue';
  import i18n from 'Utils/i18n.js';
  import { MoMABalanceText } from 'sol';

  // Form
  import ArtistFilterForm from "./artist-filter-form.vue";
  import FadeTransition from 'Components/shared/fade-transition.vue';
  import FilteredPaginationFilterCount from 'Components/filtered-pagination/filter-count.vue';
  import FilteredPaginationFilterPill from 'Components/filtered-pagination/filter-pill.vue';
  import Spinner from 'Components/shared/spinner.vue';

  // Filter/pagination
  import FilteredPaginationMixin from 'Components/filtered-pagination/filtered-pagination-mixin.js';
  import HistoryStackableMixin from 'Components/filtered-pagination/history-stackable-mixin.js';

  // Recommended list
  import RecommendContentList from './recommend_content_list.vue';

  export default {
    components: {
      ArtistFilterForm,
      FadeTransition,
      FilteredPaginationFilterCount,
      FilteredPaginationFilterPill,
      Spinner,
    },
    mixins: [
      FilteredPaginationMixin,
      HistoryStackableMixin
    ],
    props: {
      // Form
      dataExhibitionTitle: { type: String, required: true },
      dataFilteredTotal: { type: Number, required: true },
      dataTotal: { type: Number, required: true },
      paramExhibitionId: { type: Number, required: true },
      paramSearchTerm: { type: String, required: true },
      paramSortRandomSeed: { type: Number, required: true },
      paramWithOnlineWorks: { type: Boolean, required: true },

      // Recommended list
      dataRecommendedJsonUrl: { type: String, required: true },
      dataRecommendedLinkBasePath: { type: String, required: true },
      dataRecommendedPageTitle: { type: String, required: true },
    },

    data: function() {
      return {
        i18n: new i18n(),
        balanceText: new MoMABalanceText(),

        // Form
        params: {
          exhibition_id: this.paramExhibitionId,
          q: this.paramSearchTerm,
          random: this.paramSortRandomSeed,
          with_online_works: this.paramWithOnlineWorks,
        },
        exhibitionTitle: this.dataExhibitionTitle,
        filteredTotal: this.dataFilteredTotal,
        total: this.dataTotal,

        // Recommended list
        RecommendContentList: [],
      };
    },

    computed: {
      resultsEmpty() {
        return this.filteredTotal === 0 && this.params.q.length > 0;
      }
    },

    mounted() {
      // Handles navigation with back button.
      window.addEventListener("popstate", this.onHandlePopState, false);

      if (this.lastPage > 1) {
        const url = new URL(this.dataFetchBaseURL, window.location);
        const urlSearchParams = this.setUrlSearchParams(this.dataFetchBaseURL, this.params).searchParams;
        this.addPagination(url, urlSearchParams);
      }

      if (this.resultsEmpty) {
        this.fetchRecommendedList().then(this.displayRecommendedList);
      }

      if (this.$el) {
        this.balanceText.balance(this.$el.getElementsByClassName('balance-text'));
      }
    },

    beforeDestroy () {
      window.removeEventListener("popstate", this.onHandlePopState);
    },

    methods: {
      /**
       * Callback action emitted from Form child component after form submission.
       */
      onFormSubmit() {
        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from browser when history stack navigation is used (Back/Forwards buttons).
       */
      onHandlePopState() {
        const poppedUrl = new URL(window.location);
        const searchParams = this.extractSearchParamsFromUrl(poppedUrl.searchParams);
        this.params = {...this.params, ...searchParams};

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url);
      },

      /**
       * Callback action emitted from Form child component to reset reactive data.
       */
      onResetForm() {
        this.params.exhibition_id = -1;
        this.params.q = '';
        this.params.random = this.paramSortRandomSeed;
        this.params.with_online_works = true;

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from ExhibitionPill child component to reset reactive `exhibition_id` data.
       */
      onRemovePill() {
        this.params.exhibition_id = -1;

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from Form child component to update reactive `q` data.
       *
       * @param {string} val
       */
      onSearchInput(val) {
        this.params.q = val;
      },

      /**
       * Callback action emitted from Form child component to clear reactive `q` data.
       */
      onSearchReset() {
        this.params.q = '';

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from child component to update reactive `with_oneline_works` data.
       *
       * @param {boolean} val
       */
      onUpdateChecked(val) {
        this.params.with_online_works = val;

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from child component to update reactive `random` data.
       *
       * @param {number} val
       */
      onUpdateSort(val) {
        this.params.random = val;

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Extracts a predefined set of search params from a URL.
       *
       * @param {URLSearchParam} urlSearchParam
       * @returns {Object} An object composed of values extracted from search parameters or default.
       */
      extractSearchParamsFromUrl(urlSearchParam) {
        return {
          exhibition_id: urlSearchParam.get('exhibition_id') || -1,
          q: urlSearchParam.get('q') || '',
          random: parseFloat(urlSearchParam.get('random')) || -10,
          with_online_works: urlSearchParam.get('with_online_works') === "true" || false,
        };
      },

      /**
       * Wrapper for `FilteredPagination.fetchData()` to handle shared rendering callback logic.
       *
       * @param {string} url
       */
      async fetchAndDisplayResults(url) {
        this.displaySpinner();
        this.hidePaginationContainer();

        this.fetchData(url)
          .then(res => this.populatePaginationResults(res))
          .then(this.hideSpinner)
          .then(this.displayPaginationContainer)
          .then(() => {
            if (this.resultsEmpty) {
              this.fetchRecommendedList().then(this.displayRecommendedList);
            } else {
              this.removeRecommendedList();
            }
          })
          .then(() => {
            // Continue to next callback; no pagination to render if no results.
            if (this.resultsEmpty) return;

            if (this.lastPage > 1) {
              const urlSearchParams = this.setUrlSearchParams(this.dataFetchBaseURL, this.params).searchParams;
              this.addPagination(url, urlSearchParams);
            } else {
              this.removePagination();
            }
          });
      },

      /**
       * Fetch recommended list.
       */
      async fetchRecommendedList() {
        const el = document.querySelector('[data-pagination-mount]');
        if (el) el.remove();

        const url = new URL(this.dataRecommendedJsonUrl, window.location);
        const headers = new Headers({'Content-Type': 'application/json'});
        const res = await fetch(url, { headers: headers })
          .then(res => res.json())
          .then(res => this.RecommendContentList = res)
          .catch(error => {
            console.log(url + 'failed with error', error);
          });

        return res;
      },

      /**
       * Render recommended list.
       */
      displayRecommendedList() {
        new Vue({
          el: document.querySelector("section[section='artists']"),
          components: { RecommendContentList },
          data: {
            list: this.RecommendContentList,
            pageTitle: this.dataRecommendedPageTitle,
            linkBasePath: this.dataRecommendedLinkBasePath,
          },
          template: `
            <RecommendContentList
              :list=list
              :pageTitle=pageTitle
              section="artists"
              :linkBasePath=linkBasePath
              dataGtm="Recommendation / No search results"
            />
          `
        });
      },

      /**
       * Remove recommended list.
       */
      removeRecommendedList() {
        const el = document.querySelector("section[section='artists']");
        el.innerHTML = "";
        document.querySelector("[data-grid='artists']").classList.add("layout/margin:top:page:2");

        let elMount = document.querySelector('[data-pagination-mount]');
        if (elMount === null) {
          const sib = document.querySelector('[data-pagination-container]');
          elMount = document.createElement('div');
          var attr = document.createAttribute('data-pagination-mount');
          elMount.setAttributeNode(attr);
          sib.insertAdjacentElement('afterend', elMount);
        }
      },
    }
  };
</script>
